import type {
  CreateSmartFieldUiComponentTableColumnApi,
  CreateSmartFieldV1MutationRequestApi,
  EnumSmartFieldUiComponentDateTimeTypeApi,
  SmartFieldResourceWriteApi,
  SmartFieldUiComponentInputStringTypeApi,
} from '@ingenico/api-sdk-configuration-public';
import { enumSmartFieldDataType, enumSmartFieldUiComponentType } from '@ingenico/api-sdk-configuration-public';
import type {
  CreateSmartFieldSchema,
  FormBuilderTableValue,
  InputCustomModel,
  InputModel,
  InputTableModel,
  SFInputCustomModel,
  SFInputDateModel,
  SFInputFileModel,
  SFInputMultiSelectModel,
  SFInputNumberModel,
  SFInputSelectModel,
  SFInputTableColumnModel,
  SFInputTableModel,
  SFInputTextModel,
  SFInputToggleModel,
} from '@ingenico/react-form-builder';
import { InputTypeEnum, SFInputTypeEnum, getTableValue } from '@ingenico/react-form-builder';
import type { ColumnIdRecord } from '@ingenico/react-smartfield-utils';
import {
  extractFGTableValueForParameterSet,
  extractSFTableValueForParameterSet,
  transformDefaultValue,
} from '@ingenico/react-smartfield-utils';
import { toString } from 'lodash-es';
import type { SmartFieldsResource } from '../../../domain/models/smartFields.model';

export function getSmartFieldCreationInput(
  input?: InputModel<InputCustomModel>,
): InputModel<InputCustomModel> | undefined {
  if (!input || input.inputConfiguration.type !== InputTypeEnum.Table) {
    return input;
  }

  const tableConfiguration = input.inputConfiguration as InputTableModel<InputCustomModel>;
  const rules = tableConfiguration.rules;
  const requiredRule: unknown = rules?.required;
  const requiredValue =
    typeof requiredRule === 'object' && requiredRule !== null && 'value' in requiredRule
      ? requiredRule.value
      : requiredRule;
  const isRequired = requiredValue === true || requiredValue === 'true';
  const smartFieldCreationInput =
    isRequired && rules?.min === undefined
      ? {
          ...input,
          inputConfiguration: {
            ...tableConfiguration,
            rules: { ...rules, min: 1 },
          },
        }
      : input;
  const tableValue = tableConfiguration.inputValues ?? tableConfiguration.inputDefaultValue;
  const tableKey = tableValue ? Object.keys(tableValue)[0] : undefined;

  if (!tableValue || !tableKey) {
    return smartFieldCreationInput;
  }

  return {
    ...smartFieldCreationInput,
    fieldInformations: {
      ...smartFieldCreationInput.fieldInformations,
      value: normalizeStructureEmptyValues(
        extractFGTableValueForParameterSet(tableKey, tableValue),
      ) as InputModel<InputCustomModel>['fieldInformations']['value'],
    },
  };
}

// Form Builder uses a bracketed display marker; the legacy smartfield serializer
// only decodes the older unbracketed marker. Normalize cells at both modal boundaries.
function normalizeStructureEmptyValues(
  rows: ReturnType<typeof extractFGTableValueForParameterSet>,
): ReturnType<typeof extractFGTableValueForParameterSet> {
  return rows.map((row) => ({
    ...row,
    cells: row.cells.map((cell) => {
      const { fieldValue } = cell;
      if (fieldValue.type === 'STRUCTURE' && fieldValue.structure) {
        return {
          ...cell,
          fieldValue: { ...fieldValue, structure: normalizeStructureEmptyValues(fieldValue.structure) },
        };
      }
      if (fieldValue.type === 'SINGLE' && fieldValue.single === '[Empty value]') {
        return { ...cell, fieldValue: { ...fieldValue, single: '' } };
      }
      return cell;
    }),
  }));
}

export function checkIsOptionEqualToValue(option: SmartFieldsResource, inputValue: SmartFieldsResource) {
  return option?.id === inputValue?.id;
}

function getTextInputType(smartField: SFInputTextModel): SmartFieldUiComponentInputStringTypeApi {
  switch (smartField.type) {
    case 'url':
    case 'tel':
    case 'email':
    case 'text': {
      return 'TEXT';
    }
    case 'password': {
      return 'SECRET';
    }
    default: {
      return 'TEXT';
    }
  }
}

function getDatetimeFieldMode(smartField: SFInputDateModel): EnumSmartFieldUiComponentDateTimeTypeApi {
  switch (smartField.type) {
    case 'datetime': {
      return 'DATE_TIME';
    }
    case 'time': {
      return 'TIME';
    }
    case 'date': {
      return 'DATE';
    }
    default: {
      return 'DATE';
    }
  }
}
function createInputStringSmartField(form: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  const smartField = form.newSmartField as SFInputTextModel;
  return {
    dataType: enumSmartFieldDataType.STRING,
    ...(smartField.defaultValue || smartField.defaultValue === '' ? { defaultValue: smartField.defaultValue } : {}),
    description: toString(form.description),
    name: form.name,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(smartField),
  };
}

function createInputNumberSmartField(form: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  const smartField = form.newSmartField as SFInputNumberModel;
  return {
    dataType: enumSmartFieldDataType.NUMBER,
    ...(smartField.defaultValue ? { defaultValue: toString(smartField.defaultValue) } : {}),
    description: toString(form.description),
    name: form.name,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(smartField),
  };
}

function createToggleSmartField(form: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  const smartField = form.newSmartField as SFInputToggleModel;
  return {
    dataType: enumSmartFieldDataType.BOOLEAN,
    defaultValue: toString(smartField.defaultValue),
    description: toString(form.description),
    name: form.name,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(smartField),
  };
}

function createDateSmartField(form: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  const smartField = form.newSmartField as SFInputDateModel;
  return {
    dataType: enumSmartFieldDataType.DATE,
    ...(smartField.defaultValue ? { defaultValue: smartField.defaultValue } : {}),
    description: toString(form.description),
    name: form.name,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(smartField),
  };
}

function createDataFileSmartField(form: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  const smartField = form.newSmartField as SFInputFileModel;
  return {
    dataType: enumSmartFieldDataType.DATA_FILE,
    ...(smartField.defaultValue ? { defaultValue: smartField.defaultValue } : {}),
    description: toString(form.description),
    name: form.name,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(smartField),
  };
}

function extractDefaultSelectValue(smartField: SFInputSelectModel): string | undefined {
  if (smartField.defaultSelectId) {
    const matchingOption = smartField.choices.find((choice) => choice.id === smartField.defaultSelectId);
    if (matchingOption) {
      return matchingOption.value;
    }
  }
  return undefined;
}

function createSelectSmartField(form: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  const smartField = form.newSmartField as SFInputSelectModel;
  return {
    dataType: enumSmartFieldDataType.ENUM,
    defaultValue: extractDefaultSelectValue(smartField),
    description: toString(form.description),
    name: form.name,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(smartField),
  };
}

function extractDefaultMultiSelectValue(smartField: SFInputMultiSelectModel): string {
  const isMappedToSmartField = !Array.isArray(smartField.defaultSelectIds);
  if (isMappedToSmartField) {
    return '';
  }
  const mappedSelectedIds = smartField.defaultSelectIds.reduce((acc, defaultSelectedId: number) => {
    const matchingOption = smartField.choices.find((choice) => choice.id === defaultSelectedId);
    if (matchingOption) {
      acc.push(matchingOption.value);
    }
    return acc;
  }, [] as string[]);
  return JSON.stringify(mappedSelectedIds);
}

function createMultiSelectSmartField(form: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  const smartField = form.newSmartField as SFInputMultiSelectModel;
  return {
    dataType: enumSmartFieldDataType.ENUM,
    defaultValue: extractDefaultMultiSelectValue(smartField),
    description: toString(form.description),
    name: form.name,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(smartField),
  };
}

function createMultiTableSmartField(form: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  const smartField = form.newSmartField as SFInputTableModel;
  const inputDefaultValue: FormBuilderTableValue = getTableValue('', smartField.inputDefaultValue ?? []) ?? {};
  const mappedColumnIds = mapOriginalColumnId(smartField);
  const defaultValue = JSON.stringify(
    normalizeStructureEmptyValues(
      extractSFTableValueForParameterSet(
        extractTableKeyFromDefaultValue(inputDefaultValue),
        inputDefaultValue,
        mappedColumnIds,
      ),
    ),
  );
  return {
    dataType: enumSmartFieldDataType.STRUCTURE,
    defaultValue: transformDefaultValue(defaultValue, -10_000),
    description: toString(form.description),
    name: form.name,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(smartField, -10_000),
  };
}

function checkColumnIDs(value: number, offset = 0): number {
  // IMPORTANT
  // This fix avoid to create conflict between the internal index used in table and columnId.
  // The problem was that if you opened a smartfield table that was containing a nested table. If for instance
  // the nested table was the 5th items from the top, if you entered more than 6 items (index 5) in this nested table,
  // it was erased. By adding a sufficient index base value, it avoids this problem unless the user enter more
  // than 10000 rows which is unlikely.
  // The problem is initially due to the id property that is defined to be a number instead a string.
  return value + offset;
}

function mapOriginalColumnId(smartField: SFInputCustomModel): ColumnIdRecord {
  let res = {};
  if (smartField.inputType === SFInputTypeEnum.Table) {
    res = smartField.columns.reduce((acc, column, index) => {
      acc[column.id] =
        (column.uiComponent as SFInputCustomModel).inputType === SFInputTypeEnum.Table
          ? {
              id: toString(checkColumnIDs(index + 1, 10_000)),
              children: mapOriginalColumnId(column.uiComponent as SFInputCustomModel),
            }
          : { id: toString(checkColumnIDs(index + 1, 10_000)) };
      return acc;
    }, {} as ColumnIdRecord);
  }
  return res;
}

function extractTableKeyFromDefaultValue(defaultValue?: FormBuilderTableValue): string {
  return defaultValue && Object.keys(defaultValue).length > 0 ? Object.keys(defaultValue)[0] : '';
}

function mapColumn(columns: SFInputTableColumnModel[], offset: number): CreateSmartFieldUiComponentTableColumnApi[] {
  return columns.map((column, index) => ({
    ...column,
    // index starts at 1
    id: index + 1,
    uiComponent: mapSfInputToSmartFieldUiComponentApi(column.uiComponent as SFInputCustomModel, offset),
  })) as CreateSmartFieldUiComponentTableColumnApi[];
}

function mapSfInputToSmartFieldUiComponentApi(
  sfInput: SFInputCustomModel,
  offset = 0,
): SmartFieldResourceWriteApi['uiComponent'] {
  switch (sfInput.inputType) {
    case SFInputTypeEnum.Number: {
      return {
        type: enumSmartFieldUiComponentType.INPUT,
        options: {
          inputNumber: {
            ...(typeof sfInput.defaultValue === 'number' ? { defaultValue: sfInput.defaultValue } : {}),
            ...(sfInput.hint ? { hint: sfInput.hint } : {}),
            ...(sfInput.label ? { label: sfInput.label } : {}),
            ...(sfInput.placeholder ? { placeholder: sfInput.placeholder } : {}),
            ...(typeof sfInput.min === 'number' ? { min: sfInput.min } : {}),
            ...(typeof sfInput.max === 'number' ? { max: sfInput.max } : {}),
            isRequired: sfInput.isRequired ?? false,
          },
        },
      };
    }
    case SFInputTypeEnum.Text: {
      return {
        type: enumSmartFieldUiComponentType.INPUT,
        options: {
          inputString: {
            ...(sfInput.defaultValue != null ? { defaultValue: sfInput.defaultValue } : {}),
            ...(sfInput.hint ? { hint: sfInput.hint } : {}),
            ...(sfInput.label ? { label: sfInput.label } : {}),
            ...(sfInput.placeholder ? { placeholder: sfInput.placeholder } : {}),
            ...(typeof sfInput.minLength === 'number' ? { minLength: sfInput.minLength } : {}),
            ...(typeof sfInput.maxLength === 'number' ? { maxLength: sfInput.maxLength } : {}),
            isRequired: sfInput.isRequired ?? false,
            type: getTextInputType(sfInput),
            ...(sfInput.pattern
              ? {
                  pattern: {
                    value: sfInput.pattern,
                  },
                }
              : {}),
          },
        },
      };
    }
    case SFInputTypeEnum.Toggle: {
      return {
        type: enumSmartFieldUiComponentType.TOGGLE,
        options: {
          toggle: {
            ...(sfInput.hint ? { hint: sfInput.hint } : {}),
            ...(sfInput.label ? { label: sfInput.label } : {}),
            defaultValue: typeof sfInput.defaultValue === 'string' ? false : (sfInput.defaultValue ?? false),
          },
        },
      };
    }
    case SFInputTypeEnum.Select: {
      return {
        type: enumSmartFieldUiComponentType.SELECT,
        options: {
          select: {
            ...(sfInput.hint ? { hint: sfInput.hint } : {}),
            ...(sfInput.label ? { label: sfInput.label } : {}),
            ...(sfInput.label ? { placeholder: sfInput.label } : {}),
            choices: sfInput.choices,
            isRequired: sfInput.isRequired ?? false,
            ...(typeof sfInput.defaultSelectId === 'number' ? { defaultSelectedId: sfInput.defaultSelectId } : {}),
          },
        },
      };
    }
    case SFInputTypeEnum.MultiSelect: {
      return {
        type: enumSmartFieldUiComponentType.MULTI_SELECT,
        options: {
          multiSelect: {
            ...(sfInput.hint ? { hint: sfInput.hint } : {}),
            ...(sfInput.label ? { label: sfInput.label } : {}),
            ...(sfInput.label ? { placeholder: sfInput.label } : {}),
            choices: sfInput.choices,
            isRequired: sfInput.isRequired ?? false,
            defaultSelectedIds: typeof sfInput.defaultSelectIds === 'string' ? [] : sfInput.defaultSelectIds,
          },
        },
      };
    }
    case SFInputTypeEnum.Date: {
      return {
        type: enumSmartFieldUiComponentType.DATE_TIME,
        options: {
          dateTime: {
            ...(sfInput.hint ? { hint: sfInput.hint } : {}),
            ...(sfInput.label ? { label: sfInput.label } : {}),
            endDate: sfInput.endDate,
            startDate: sfInput.startDate,
            type: getDatetimeFieldMode(sfInput),
            isRequired: sfInput.isRequired ?? false,
            format: sfInput.format,
            ...(sfInput.defaultValue ? { defaultValue: sfInput.defaultValue } : {}),
          },
        },
      };
    }
    case SFInputTypeEnum.File: {
      return {
        type: enumSmartFieldUiComponentType.DATA_FILE,
        options: {
          dataFile: {
            ...(sfInput.label ? { label: sfInput.label } : {}),
            ...(sfInput.maxSize != null ? { maxSize: sfInput.maxSize } : {}),
            acceptedExtensions: sfInput.acceptedExtensions ?? [],
            isRequired: sfInput.isRequired ?? false,
          },
        },
      };
    }
    case SFInputTypeEnum.Table: {
      return {
        type: enumSmartFieldUiComponentType.TABLE,
        options: {
          table: {
            ...(typeof sfInput.min === 'number' ? { min: sfInput.min } : {}),
            ...(typeof sfInput.max === 'number' ? { max: sfInput.max } : {}),
            columns: mapColumn(sfInput.columns, offset),
          },
        },
      };
    }
  }
}

export function generateCreateSmartFieldBody(smartField: CreateSmartFieldSchema): CreateSmartFieldV1MutationRequestApi {
  switch (smartField.newSmartField.inputType) {
    case SFInputTypeEnum.Text: {
      return createInputStringSmartField(smartField);
    }
    case SFInputTypeEnum.Number: {
      return createInputNumberSmartField(smartField);
    }
    case SFInputTypeEnum.Toggle: {
      return createToggleSmartField(smartField);
    }
    case SFInputTypeEnum.Date: {
      return createDateSmartField(smartField);
    }
    case SFInputTypeEnum.File: {
      return createDataFileSmartField(smartField);
    }
    case SFInputTypeEnum.Select: {
      return createSelectSmartField(smartField);
    }
    case SFInputTypeEnum.MultiSelect: {
      return createMultiSelectSmartField(smartField);
    }
    case SFInputTypeEnum.Table: {
      return createMultiTableSmartField(smartField);
    }
    default: {
      throw new Error('Not yet implemented');
    }
  }
}
